import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Form, Button, notification } from "antd";
import { useLocation } from "react-router-dom";
import { isEmailValid } from "../../../helpers/ValidationHelpers";
import CustomButton from "../../../common/Buttons/CustomButton";
import { backArrowIcon } from "../../../assets/images";
import styles from "./PersonForm.module.scss";
import ContactDetails from "./ContactDetails";
import DuplicateTrustedContactModal from "../Modals/DuplicateTrustedContactModal";
import {
  GeneralDefinition,
  ServicesTypesComponentLoad,
} from "../../../types/ApplicationTypes";
import { usePersonCreation } from "../../../hooks/usePersonCreation";
import { useServiceUserCreationCtx } from "../../../context/ServiceUserCreationContext";
import GoogleLoader from "../../../assets/Loader/GoogleSvgLoader/GoogleLoader";
import { useHistory } from "react-router";
import { usePersonSummaryCtx } from "../../../context/PersonSummaryContext";

export type Contact = {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  relationship: number;
};

type Props = {
  setPageType: Function;
};

function TrustedContactsForm({ setPageType }: Props) {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isDuplicateTcModalVisible, setIsDuplicateTcModalVisible] = useState<
    boolean
  >(false);
  const [shouldDisableBtn, setShouldDisableBtn] = useState<boolean>(true);
  const {
    whoWillUseServicesCtx,
    registrationBackBtnClickCtx,
    serviceUserMyselfDetailsCtx,
    serviceUserSomeoneElseDetailsCtx,
    contactTcCtx,
    isLoadingCtx,
  } = useServiceUserCreationCtx();
  const [serviceUserMyselfDetails] = serviceUserMyselfDetailsCtx;
  const [serviceUserSomeoneElseDetails] = serviceUserSomeoneElseDetailsCtx;
  const [, setIsBackBtnClick] = registrationBackBtnClickCtx;
  const [whoWillUseServices] = whoWillUseServicesCtx;
  const [contacts, setContacts] = contactTcCtx;
  const [isLoading, setIsLoading] = isLoadingCtx;
  const { caregiverSummaryInfo } = usePersonSummaryCtx();
  const { handleCreatePerson } = usePersonCreation();
  const history = useHistory();

  const MAX_USERS = 25;
  const MAX_TRUSTED_CONTACTS = 5;

  const [form] = Form.useForm();
  const location = useLocation();

  useEffect(() => {
    if (contacts.length > 0) {
      setCurrentIndex(contacts.length);
    }
  }, []);

  const serviceUserfirstName = useMemo(() => {
    return !["SomeoneElse", "Profile"].includes(whoWillUseServices)
      ? caregiverSummaryInfo?.firstName
      : serviceUserSomeoneElseDetails?.createPersonData?.firstName;
  }, []);

  function navigateToNextContact() {
    if (currentIndex + 1 < contacts.length) {
      setCurrentIndex((prevIndex) => prevIndex + 1);
      form.setFieldsValue(contacts[currentIndex + 1]);
    } else if (currentIndex + 1 < MAX_TRUSTED_CONTACTS) {
      form.resetFields();
      setCurrentIndex((prevIndex) => prevIndex + 1);
    }
    validateAndCheckForm();
  }

  function isDuplicateTC(newContact: Contact) {
    return contacts.some((contact: Contact, index: number) => {
      const isDuplicateContact =
        index !== currentIndex &&
        (Object.keys(newContact) as Array<keyof Contact>).every((key) => {
          if (key !== "relationship") {
            return newContact[key] === contact[key];
          } else return true;
        });

      return isDuplicateContact;
    });
  }

  function handleSavePlusAddAnother() {
    const values = form.getFieldsValue(true);

    if (contacts.length > 0 && isDuplicateTC(values)) {
      setIsDuplicateTcModalVisible(true);
    } else {
      const newContacts = [...contacts];
      newContacts[currentIndex] = values;
      setContacts(newContacts);

      navigateToNextContact();
    }
  }

  async function handleSkipForNow() {
    await redirectToNextPage(contacts);
  }

  async function handleFinish(lastContactValues: Contact) {
    const totalContacts = [...contacts];
    totalContacts[currentIndex] = lastContactValues;
    if (contacts.length > 0 && isDuplicateTC(lastContactValues)) {
      setIsDuplicateTcModalVisible(true);
    } else {
      await redirectToNextPage(totalContacts);
    }
  }

  async function validateAndCheckForm() {
    const {
      phone,
      email,
      firstName,
      lastName,
      relationship,
    } = form.getFieldsValue(true);

    const isBasicInfoValid = [firstName, lastName, relationship].every(Boolean);
    const isValidPhone = phone && phone.length === 12;
    const isValidEmail = email && isEmailValid(email);

    const isInvalidFieldExists = !(
      isBasicInfoValid &&
      isValidPhone &&
      isValidEmail
    );

    isInvalidFieldExists
      ? setShouldDisableBtn(true)
      : setShouldDisableBtn(false);
  }

  function handleValuesChange(changedValues: any, allValues: Contact) {
    validateAndCheckForm();
  }

  function handleBack() {
    if (currentIndex > 0) {
      setCurrentIndex((prevIndex) => prevIndex - 1);
      form.setFieldsValue(contacts[currentIndex - 1]);
      validateAndCheckForm();
    } else {
      if (["ProfileMyself", "Myself"].includes(whoWillUseServices)) {
        setPageType(ServicesTypesComponentLoad.MyselfWillUseServices);
      } else if (["MemberProfile", "Member"].includes(whoWillUseServices)) {
        setPageType(ServicesTypesComponentLoad.MTMBenefitCheck);
      } else {
        setPageType(ServicesTypesComponentLoad.SomeoneElseWillUseServices);
      }
      setIsBackBtnClick(true);
    }
  }

  async function redirectToNextPage(contacts: Contact[]) {
    const queryParams = new URLSearchParams(location.search);
    const source = queryParams.get("source");
    try {
      setIsLoading(true);
      let serviceUserDetails;
      if (["Myself", "ProfileMyself"].includes(whoWillUseServices)) {
        serviceUserDetails = serviceUserMyselfDetails;
      } else {
        serviceUserDetails = serviceUserSomeoneElseDetails;
      }
      const usersCount = await handleCreatePerson(contacts, serviceUserDetails);
      // Maximum 25 service users can be added to an account
      if (usersCount) {
        if (
          whoWillUseServices === GeneralDefinition.SomeoneElse ||
          (usersCount === MAX_USERS && source !== "Dashboard")
        ) {
          setPageType(ServicesTypesComponentLoad.AddAnotherServiceUser);
        } else if (
          ["Myself", "Member"].includes(whoWillUseServices) ||
          (whoWillUseServices === GeneralDefinition.SomeoneElse &&
            usersCount === MAX_USERS)
        ) {
          setPageType(ServicesTypesComponentLoad.WandaPlusSubscription);
        } else if (
          ["Profile", "ProfileMyself", "MemberProfile"].includes(
            whoWillUseServices
          )
        ) {
          history.push("/DashBoard");
        }
      }
      setContacts([]);
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to redirect to next page.",
      });
    }
  }

  return isLoading ? (
    <GoogleLoader />
  ) : (
    <>
      <Row className={styles.tcLayoutAlign}>
        <Col xs={2} lg={4} />
        <Col xs={20} lg={16}>
          <CustomButton
            text="Back"
            handlerFunction={handleBack}
            type="link"
            icon={backArrowIcon}
            className="backBtn"
          />

          <Form
            form={form}
            name="personRelationForm"
            layout="vertical"
            onFinish={handleFinish}
            onValuesChange={handleValuesChange}
            className={styles.mobileFormAlign}
          >
            <Row justify="center" gutter={[10, 15]}>
              <Col span={24}>
                {!["SomeoneElse", "Profile"].includes(whoWillUseServices) ? (
                  <Row gutter={[0, 20]} className={styles.title}>
                    Scheduling Services for Myself <Col xs={0} lg={24} />
                  </Row>
                ) : (
                  <Row gutter={[0, 20]} className={styles.title}>
                    Scheduling Services for Someone Else <Col xs={0} lg={24} />
                  </Row>
                )}
              </Col>
              <Col xs={0} lg={6} />
              <Col xs={24} lg={12} className={styles.contactDetailsAlign}>
                <Row className={styles.label} justify="center">
                  <Col className={styles.labelAlign}>
                    Add a trusted contact for{" "}
                    {!["SomeoneElse", "Profile"].includes(whoWillUseServices)
                      ? "yourself"
                      : serviceUserfirstName}
                    ?
                  </Col>
                </Row>

                <Row
                  className={styles.labelDescription}
                  justify="center"
                  gutter={[0, 20]}
                >
                  <Col className={styles.labelDescriptionAlign}>
                    A trusted contact is someone other than the caregiver who
                    will also receive communications about{" "}
                    {serviceUserfirstName}
                    's transportation & other services.
                  </Col>
                  <Col span={24} />
                </Row>

                <ContactDetails />
              </Col>
              <Col xs={0} lg={6} />

              <Col>
                <Button
                  className={styles.skipForNow}
                  onClick={handleSkipForNow}
                >
                  Skip For Now
                </Button>
              </Col>
              <Col>
                {currentIndex + 1 < MAX_TRUSTED_CONTACTS && (
                  <Button
                    className={`${
                      styles.savePlusAddAnother
                    } ${shouldDisableBtn && styles.disableBtn}`}
                    onClick={handleSavePlusAddAnother}
                    disabled={shouldDisableBtn}
                  >
                    Save + Add Another
                  </Button>
                )}
              </Col>
              <Col>
                <Button
                  className={`${styles.save} ${shouldDisableBtn &&
                    styles.disableBtn}`}
                  htmlType="submit"
                  disabled={shouldDisableBtn}
                >
                  Save
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>
        <Col xs={2} lg={4} />
      </Row>

      <DuplicateTrustedContactModal
        form={form}
        visible={isDuplicateTcModalVisible}
        serviceUserfirstName={serviceUserfirstName}
        setVisibility={setIsDuplicateTcModalVisible}
        validateForm={validateAndCheckForm}
      />
    </>
  );
}

export default TrustedContactsForm;
